import { apiFetch } from '../../shared/apiClient';
import type { DeliveryReviewDimensionKey } from '../../domain/delivery-intelligence/types';

export interface PillarWeightsSettings {
  commitment: number;
  delivery_confidence: number;
  capacity: number;
  quality: number;
}

export interface PillarWeightsOverride {
  productId: string;
  productName: string;
  settings: PillarWeightsSettings;
}

export interface EffectivePillarWeightsSettings {
  settings: PillarWeightsSettings;
  isOverride: boolean;
}

export const DEFAULT_PILLAR_WEIGHTS: PillarWeightsSettings = {
  commitment: 40,
  delivery_confidence: 30,
  capacity: 15,
  quality: 15,
};

export const PillarWeightsApi = {
  async getOrgSettings(): Promise<{ settings: PillarWeightsSettings }> {
    const res = await apiFetch('/api/pillar-weights/settings/org');
    if (!res.ok) throw new Error(`Failed to fetch org pillar weights: ${res.statusText}`);
    return res.json();
  },

  async getOverrides(): Promise<{ overrides: PillarWeightsOverride[] }> {
    const res = await apiFetch('/api/pillar-weights/settings/overrides');
    if (!res.ok) throw new Error(`Failed to fetch pillar weights overrides: ${res.statusText}`);
    return res.json();
  },

  async saveOrgSettings(
    settings: PillarWeightsSettings
  ): Promise<{ settings: PillarWeightsSettings }> {
    const res = await apiFetch('/api/pillar-weights/settings/org', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save org pillar weights: ${res.statusText}`);
    }
    return res.json();
  },

  async saveOverride(
    productId: string,
    settings: PillarWeightsSettings
  ): Promise<{ settings: PillarWeightsSettings; isOverride: boolean }> {
    const res = await apiFetch(
      `/api/pillar-weights/settings/overrides/${encodeURIComponent(productId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(settings),
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save pillar weights override: ${res.statusText}`);
    }
    return res.json();
  },

  async deleteOverride(productId: string): Promise<void> {
    const res = await apiFetch(
      `/api/pillar-weights/settings/overrides/${encodeURIComponent(productId)}`,
      { method: 'DELETE' }
    );
    if (!res.ok) throw new Error(`Failed to reset pillar weights override: ${res.statusText}`);
  },

  async getEffectiveSettings(): Promise<EffectivePillarWeightsSettings> {
    const res = await apiFetch('/api/pillar-weights/settings/effective');
    if (!res.ok) {
      throw new Error(`Failed to fetch effective pillar weights: ${res.statusText}`);
    }
    return res.json();
  },
};

/** Convert settings shape to the Record<DimensionKey, number> used by scoring engine. */
export function pillarWeightsToRecord(
  weights: PillarWeightsSettings
): Record<DeliveryReviewDimensionKey, number> {
  return {
    commitment: weights.commitment,
    delivery_confidence: weights.delivery_confidence,
    capacity: weights.capacity,
    quality: weights.quality,
  };
}
